import { v4 as uuidv4 } from 'uuid';
import { action, computed, observable } from 'mobx';
import { message } from 'antd';
import { DataCrafterTemplatesService } from '../services';
import { DataCrafterTemplateColumn, DataCrafterOutputVariant, JobStatus, ScriptGenerationResponse } from '../types';
import { throttle } from 'lodash';
import CodeValidationContext from '../types/CodeValidationContext';
import { TestPickerScriptResponse } from '../types';
import { Utils } from '../../common/services/Utils';
import type { IncrementalCompilationMode } from '../services/DataCrafterTemplatesService';

/**
 * Interface for tracking compilation progress during script generation
 */
interface CompileProgress {
    step: number;
    total_steps: number;
    message: string;
    status: string;
    duration: number;
    step_details?: {
        model_output?: string;
        observations?: string;
        action_output?: string | null;
        tool_calls?: Array<unknown>;
    };
}

/**
 * Interface for managing template compilation state
 */
interface TemplateCompilationState {
    templateScript: string | null;
    templateScriptRefId: string | null;
    isPreparingCompilation: boolean;
    isLoadingTemplateScript: boolean;
    compilationProgress: CompileProgress | null;
    templateScriptError: string | undefined;
    isLoadingIncrementalTemplateScript: boolean;
    incrementalTemplateScriptError: string | undefined;
    incrementalCompilationMode: IncrementalCompilationMode | undefined;
    incrementalCandidateTargets: string[];
    // Inputs of the last successful compile in this visit; reopening the template clears them.
    lastCompiledInputs: string | undefined;
}

/**
 * Default state for template compilation
 */
const defaultTemplateCompilationState: TemplateCompilationState = {
    templateScript: null,
    templateScriptRefId: null,
    isPreparingCompilation: false,
    isLoadingTemplateScript: false,
    compilationProgress: null,
    templateScriptError: undefined,
    isLoadingIncrementalTemplateScript: false,
    incrementalTemplateScriptError: undefined,
    incrementalCompilationMode: undefined,
    incrementalCandidateTargets: [] as string[],
    lastCompiledInputs: undefined
} as const;

/**
 * Store responsible for managing script compilation functionality
 * Handles both template script compilation and variant picker script compilation
 */
export default class DataCrafterScriptCompilationStore {
    // ========== Observable Properties ==========

    @observable
    isPreviewLoading: boolean = false;

    @observable
    isValidateLoading: boolean = false;

    @observable
    isTestingPickerScript: boolean = false;

    @observable
    pickerScriptTestResult: TestPickerScriptResponse | null = null;

    @observable
    codeValidationContext: CodeValidationContext | undefined = undefined;

    // Temp id of the template that is being created (used to store the compilation state)
    @observable
    tempTemplateId: string = this.generateTempId();

    @observable
    private templateCompilationStates = observable.map<string, TemplateCompilationState>();

    @observable
    private templateVariantPickerCompilationStates = observable.map<string, TemplateCompilationState>();

    private readonly tempTemplateIdPrefix = 'temp-template-';
    private readonly templateJobPollingDelay = 1000;
    private readonly previewFileName = 'preview.xlsx';

    // ========== Constructor ==========

    constructor(private dataCrafterTemplatesService: DataCrafterTemplatesService) {}

    // ========== Computed Properties ==========

    @computed
    get currentTemplateCompilationState(): TemplateCompilationState {
        return this.templateCompilationStates.get(this.tempTemplateId) ?? { ...defaultTemplateCompilationState };
    }

    @computed
    get currentTemplateVariantPickerCompilationState(): TemplateCompilationState {
        return (
            this.templateVariantPickerCompilationStates.get(this.tempTemplateId) ?? {
                ...defaultTemplateCompilationState
            }
        );
    }

    @computed
    get currentTemplateScript(): string | null {
        return this.currentTemplateCompilationState.templateScript;
    }

    @computed
    get currentTemplateScriptRefId(): string | null {
        return this.currentTemplateCompilationState.templateScriptRefId;
    }

    @computed
    get currentTemplateScriptError(): string | undefined {
        return this.currentTemplateCompilationState.templateScriptError;
    }

    @computed
    get currentCompilationProgress(): CompileProgress | null {
        return this.currentTemplateCompilationState.compilationProgress;
    }

    @computed
    get isCurrentTemplateLoading(): boolean {
        return (
            this.currentTemplateCompilationState.isLoadingTemplateScript ||
            this.currentTemplateCompilationState.isLoadingIncrementalTemplateScript
        );
    }

    @computed
    get isCurrentTemplatePreparingCompilation(): boolean {
        return this.currentTemplateCompilationState.isPreparingCompilation;
    }

    @computed
    get isCurrentTemplateIncrementalLoading(): boolean {
        return this.currentTemplateCompilationState.isLoadingIncrementalTemplateScript;
    }

    @computed
    get currentIncrementalCompilationMode(): IncrementalCompilationMode | undefined {
        return this.currentTemplateCompilationState.incrementalCompilationMode;
    }

    @computed
    get currentIncrementalTemplateScriptError(): string | undefined {
        return this.currentTemplateCompilationState.incrementalTemplateScriptError;
    }

    @computed
    get isCurrentTemplateVariantPickerLoading(): boolean {
        return this.currentTemplateVariantPickerCompilationState.isLoadingTemplateScript;
    }

    @computed
    get isBusyWithScriptOperation(): boolean {
        return (
            this.isCurrentTemplateLoading ||
            this.isCurrentTemplatePreparingCompilation ||
            this.isPreviewLoading ||
            this.isValidateLoading ||
            this.isTestingPickerScript ||
            this.isCurrentTemplateVariantPickerLoading
        );
    }

    // ========== Actions ==========

    @action.bound
    generateTempTemplateId() {
        this.tempTemplateId = this.generateTempId();
    }

    @action.bound
    setCodeValidationContext(context: CodeValidationContext | undefined) {
        this.codeValidationContext = context;
    }

    @action.bound
    setIsPreviewLoading(isLoading: boolean) {
        this.isPreviewLoading = isLoading;
    }

    @action.bound
    setIsValidateLoading(isLoading: boolean) {
        this.isValidateLoading = isLoading;
    }

    @action.bound
    setIsTestingPickerScript(isLoading: boolean) {
        this.isTestingPickerScript = isLoading;
    }

    @action.bound
    setPickerScriptTestResult(result: TestPickerScriptResponse | null) {
        this.pickerScriptTestResult = result;
    }

    @action.bound
    setTemplateScriptError(templateId: string, error: string | undefined) {
        this.setTemplateCompilationState(templateId, { templateScriptError: error });
    }

    @action.bound
    setIsPreparingCompilation(templateId: string, isPreparing: boolean) {
        this.setTemplateCompilationState(templateId, { isPreparingCompilation: isPreparing });
    }

    @action.bound
    setTemplateCompilationState(templateId: string, state: Partial<TemplateCompilationState>) {
        const currentState = this.templateCompilationStates.get(templateId) ?? { ...defaultTemplateCompilationState };
        this.templateCompilationStates.set(templateId, {
            ...currentState,
            ...state
        });
    }

    @action.bound
    setTemplateVariantPickerCompilationState(templateId: string, state: Partial<TemplateCompilationState>) {
        const currentState = this.templateVariantPickerCompilationStates.get(templateId) ?? {
            ...defaultTemplateCompilationState
        };
        this.templateVariantPickerCompilationStates.set(templateId, {
            ...currentState,
            ...state
        });
    }

    @action.bound
    markIncrementalCandidate(templateId: string, outputVariantId?: string) {
        const currentState = this.getTemplateCompilationState(templateId);
        const target = outputVariantId ?? 'main';

        if (!currentState.incrementalCandidateTargets.includes(target)) {
            this.setTemplateCompilationState(templateId, {
                incrementalCandidateTargets: [...currentState.incrementalCandidateTargets, target]
            });
        }
    }

    @action.bound
    clearIncrementalCandidate(templateId: string, outputVariantId?: string) {
        const currentState = this.getTemplateCompilationState(templateId);
        const target = outputVariantId ?? 'main';

        this.setTemplateCompilationState(templateId, {
            incrementalCandidateTargets: currentState.incrementalCandidateTargets.filter(item => item !== target)
        });
    }

    @action.bound
    clearIncrementalCandidates(templateId: string) {
        this.setTemplateCompilationState(templateId, { incrementalCandidateTargets: [] });
    }

    hasIncrementalCandidate(templateId: string, outputVariantId?: string): boolean {
        const target = outputVariantId ?? 'main';
        return this.getTemplateCompilationState(templateId).incrementalCandidateTargets.includes(target);
    }

    hasAnyIncrementalCandidate(templateId: string): boolean {
        return this.getTemplateCompilationState(templateId).incrementalCandidateTargets.length > 0;
    }

    @action.bound
    setCurrentTemplateId(templateId: string) {
        this.tempTemplateId = templateId;
    }

    // ========== Script Compilation Methods ==========

    @action.bound
    async runTemplateScriptJob(
        projectId: string,
        templateId: string,
        selectedInputPackages: string[],
        selectedOutputPackage: string,
        templateScriptRefId: string | null,
        outputVariants: DataCrafterOutputVariant[],
        variantPickerScriptRefId?: string | null,
        selectedVariantId?: string
    ): Promise<void> {
        try {
            this.setIsPreviewLoading(true);
            this.setTemplateScriptError(this.tempTemplateId, undefined);

            const job = await this.dataCrafterTemplatesService.createTemplateScriptRunJob(
                projectId,
                templateId,
                selectedInputPackages,
                selectedOutputPackage,
                templateScriptRefId,
                outputVariants.map(variant => ({
                    variantId: variant.variantId,
                    name: variant.name,
                    condition: variant.condition,
                    isDefault: variant.isDefault,
                    scriptRefId: variant.scriptRefId
                })),
                variantPickerScriptRefId || null,
                selectedVariantId
            );

            const blob = await this.waitForAsyncTemplateResult(job.jobId);
            Utils.downloadBlob(blob, this.previewFileName);
            message.success('Template script preview generated successfully');
        } catch (error) {
            console.error('Failed to run template script:', error);
            const errorMessage = (error as Error).message || 'Unknown error';
            message.error('Failed to run template script: ' + errorMessage);
            this.setTemplateScriptError(this.tempTemplateId, 'Failed to run template script: ' + errorMessage);
        } finally {
            this.setIsPreviewLoading(false);
        }
    }

    @action.bound
    async validateTemplateScript(
        selectedInputPackages: string[],
        selectedOutputPackage: string,
        templateScript: string,
        outputColumns: DataCrafterTemplateColumn[],
        modelId: string
    ): Promise<void> {
        try {
            this.setTemplateScriptError(this.tempTemplateId, undefined);
            this.setCodeValidationContext(undefined);
            this.setIsValidateLoading(true);

            const response = await this.dataCrafterTemplatesService.validateTemplateScript(
                selectedInputPackages,
                selectedOutputPackage,
                templateScript,
                outputColumns.filter(col => col.mappingRule),
                modelId
            );

            const context = new CodeValidationContext(response.code_analysis_result, response.rule_analysis);
            this.setCodeValidationContext(context);

            message.success('Script validation completed successfully');
        } catch (error) {
            console.error('Failed to validate template script:', error);
            message.error('Failed to validate template script');
            this.setTemplateScriptError(
                this.tempTemplateId,
                'Failed to validate template script: ' + ((error as Error).message || 'Unknown error')
            );
        } finally {
            this.setIsValidateLoading(false);
        }
    }

    @action.bound
    async compileTemplateScriptWithProgress(
        selectedInputPackages: string[],
        selectedOutputPackage: string,
        outputColumns: DataCrafterTemplateColumn[],
        plannerModelId: string | null,
        coderModelId: string | null,
        templateId: string,
        projectId: string,
        selectedOutputVariant?: DataCrafterOutputVariant
    ): Promise<void> {
        this.beginTemplateCompilation(templateId, false);

        let streamingBuffer = '';

        const updateStreamMessage = throttle((msg: string) => {
            const currentState = this.templateCompilationStates.get(templateId) ?? {
                ...defaultTemplateCompilationState
            };

            this.setTemplateCompilationState(templateId, {
                compilationProgress: {
                    ...currentState.compilationProgress!,
                    message: msg
                }
            });
        }, 100);

        try {
            const result = await this.dataCrafterTemplatesService.compileTemplateScriptStream(
                selectedInputPackages,
                selectedOutputPackage,
                outputColumns.filter(col => col.mappingRule),
                plannerModelId,
                coderModelId,
                projectId,
                templateId.startsWith(this.tempTemplateIdPrefix) ? null : templateId,
                {
                    onProgress: progress => {
                        this.setTemplateCompilationState(templateId, {
                            compilationProgress: {
                                ...progress,
                                duration: progress.duration ?? 0,
                                step_details: progress.step_details ?? undefined
                            }
                        });
                    },
                    onStreaming: delta => {
                        streamingBuffer += delta;
                        updateStreamMessage(streamingBuffer);
                    }
                },
                selectedOutputVariant?.variantId
            );

            this.setCompiledTemplate(templateId, selectedOutputVariant, result.scriptRefId, result.code);
            this.completeTemplateCompilation(templateId, false);
            this.clearIncrementalCandidate(templateId, selectedOutputVariant?.variantId);
        } catch (error) {
            const reason = error instanceof Error ? error.message : String(error ?? 'Unknown error');
            console.error('Failed to compile template script:', error);
            this.failTemplateCompilation(templateId, false, `Failed to compile template script: ${reason}`);
        } finally {
            updateStreamMessage.cancel();
        }
    }

    @action.bound
    async compileTemplateScriptIncrementallyWithProgress(
        plannerModelId: string | null,
        coderModelId: string | null,
        templateId: string,
        projectId: string,
        selectedOutputVariant?: DataCrafterOutputVariant,
        compileInputs?: string
    ): Promise<void> {
        // Compiling again without changes means the user rejects the cached or patched result.
        const forceFull =
            compileInputs !== undefined &&
            this.getTemplateCompilationState(templateId).lastCompiledInputs === compileInputs;
        this.beginTemplateCompilation(templateId, true);
        let finalState: Partial<TemplateCompilationState> = {};

        try {
            const result = await this.dataCrafterTemplatesService.compileTemplateScriptIncrementallyStream(
                projectId,
                templateId,
                plannerModelId,
                coderModelId,
                selectedOutputVariant?.variantId,
                forceFull
            );

            this.setCompiledTemplate(templateId, selectedOutputVariant, result.scriptRefId, result.code);

            finalState = {
                incrementalTemplateScriptError: undefined,
                incrementalCompilationMode: result.compilationMode,
                lastCompiledInputs: compileInputs
            };

            // A script that differs from the saved one stays unaccepted until Update Template saves it,
            // including no_change after an unsaved full result. Older services omit savedCodeCurrent.
            const isSaved = result.savedCodeCurrent ?? result.compilationMode === 'no_change';
            if (isSaved) {
                this.clearIncrementalCandidate(templateId, selectedOutputVariant?.variantId);
            } else {
                this.markIncrementalCandidate(templateId, selectedOutputVariant?.variantId);
            }

            if (result.compilationMode === 'no_change') {
                message.success(
                    isSaved
                        ? 'Template script is up to date with the mappings'
                        : 'Template script is up to date with the mappings; Update Template to save it'
                );
            } else {
                message.success(
                    `Template script compilation completed (${result.compilationMode === 'incremental' ? 'incremental' : 'full'})`
                );
            }
        } catch (error) {
            const reason = error instanceof Error ? error.message : String(error ?? 'Unknown error');
            const errorMessage = `Failed to compile template script: ${reason}`;

            finalState = {
                templateScriptError: errorMessage,
                incrementalTemplateScriptError: errorMessage,
                // A failed forced rebuild keeps its inputs so the retry is still full; failed changed inputs must
                // not force a later compile of the earlier inputs.
                lastCompiledInputs: forceFull ? compileInputs : undefined
            };

            message.error(errorMessage);
        } finally {
            this.completeTemplateCompilation(templateId, true, finalState);
        }
    }

    @action.bound
    async compileTemplateVariantPickerScriptWithProgress(
        outputVariants: DataCrafterOutputVariant[],
        coderModelId: string,
        templateId: string,
        projectId: string
    ): Promise<void> {
        try {
            // Clear previous test result since compilation will generate new script
            this.setPickerScriptTestResult(null);

            this.setTemplateVariantPickerCompilationState(templateId, {
                isLoadingTemplateScript: true,
                compilationProgress: {
                    step: 1,
                    total_steps: 1,
                    message:
                        'Initializing code generation...\nProcessing template configuration and preparing initial prompt.\n',
                    status: 'in_progress',
                    duration: 0
                },
                templateScriptError: undefined
            });

            const response: ScriptGenerationResponse =
                await this.dataCrafterTemplatesService.compileTemplateVariantPickerScript(
                    outputVariants.map(variant => ({
                        variantId: variant.variantId,
                        name: variant.name,
                        condition: variant.condition,
                        isDefault: variant.isDefault,
                        scriptRefId: variant.scriptRefId
                    })),
                    coderModelId,
                    projectId,
                    templateId.startsWith(this.tempTemplateIdPrefix) ? undefined : templateId
                );

            this.setTemplateVariantPickerCompilationState(templateId, {
                templateScript:
                    response.code ?? this.getTemplateVariantPickerCompilationState(templateId).templateScript,
                templateScriptRefId: response.scriptRefId || null,
                isLoadingTemplateScript: false,
                compilationProgress: null
            });
        } catch (error) {
            console.error('Failed to compile picker script:', error);
            this.setTemplateVariantPickerCompilationState(templateId, {
                templateScriptError: `Failed to compile picker script: ${error.message}`,
                isLoadingTemplateScript: false,
                compilationProgress: null
            });
        }
    }

    @action.bound
    async testTemplateVariantPickerScript(
        projectId: string,
        templateId: string,
        selectedInputPackages: string[],
        pickerScriptRefId: string,
        outputVariants: DataCrafterOutputVariant[]
    ): Promise<void> {
        try {
            this.setIsTestingPickerScript(true);
            this.setPickerScriptTestResult(null);

            const response = await this.dataCrafterTemplatesService.testTemplateVariantPickerScript(
                projectId,
                templateId,
                selectedInputPackages,
                pickerScriptRefId,
                outputVariants.map(variant => ({
                    variantId: variant.variantId,
                    name: variant.name,
                    condition: variant.condition,
                    isDefault: variant.isDefault,
                    scriptRefId: variant.scriptRefId
                }))
            );

            this.setPickerScriptTestResult(response);
        } catch (error) {
            console.error('Failed to test picker script:', error);
            message.error('Failed to test picker script');
        } finally {
            this.setIsTestingPickerScript(false);
        }
    }

    // ========== Utility Methods ==========

    @action.bound
    reset() {
        // Reset loading states
        this.isPreviewLoading = false;
        this.isValidateLoading = false;
        this.isTestingPickerScript = false;

        // Reset picker script test result
        this.pickerScriptTestResult = null;

        // Reset compilation states for temp templates
        this.templateCompilationStates.forEach((_, key) => {
            if (key.startsWith(this.tempTemplateIdPrefix)) {
                this.templateCompilationStates.delete(key);
            }
        });

        this.templateVariantPickerCompilationStates.forEach((_, key) => {
            if (key.startsWith(this.tempTemplateIdPrefix)) {
                this.templateVariantPickerCompilationStates.delete(key);
            }
        });

        this.setCodeValidationContext(undefined);
    }

    @action.bound
    initializeWithTemplate(templateId: string, templateCode?: string, variantPickerScript?: string) {
        this.setCurrentTemplateId(templateId);

        const current = this.getTemplateCompilationState(templateId);
        const isCompiling =
            current.isPreparingCompilation ||
            current.isLoadingTemplateScript ||
            current.isLoadingIncrementalTemplateScript;

        // The saved code is the source of truth when a template opens; an unsaved result from an earlier visit
        // would otherwise be shown but not saved by Update Template. An in-flight compilation publishes its own result.
        if (!isCompiling) {
            this.templateCompilationStates.set(templateId, {
                ...defaultTemplateCompilationState,
                templateScript: templateCode ?? null
            });
        }

        this.setTemplateVariantPickerCompilationState(templateId, {
            templateScript: variantPickerScript || undefined,
            templateScriptRefId: null
        });
    }

    /**
     * Get compilation state for a specific template
     */
    getTemplateCompilationState(templateId: string): TemplateCompilationState {
        return this.templateCompilationStates.get(templateId) ?? { ...defaultTemplateCompilationState };
    }

    /**
     * Get variant picker compilation state for a specific template
     */
    getTemplateVariantPickerCompilationState(templateId: string): TemplateCompilationState {
        return this.templateVariantPickerCompilationStates.get(templateId) ?? { ...defaultTemplateCompilationState };
    }

    /**
     * Get the display script for a template (considering output variants)
     */
    getDisplayTemplateScript(templateId: string, selectedOutputVariant?: DataCrafterOutputVariant): string | null {
        return selectedOutputVariant?.code ?? this.getTemplateCompilationState(templateId).templateScript;
    }

    getTemplateScriptRefId(templateId: string, selectedOutputVariant?: DataCrafterOutputVariant): string | null {
        return selectedOutputVariant?.scriptRefId ?? this.getTemplateCompilationState(templateId).templateScriptRefId;
    }

    getVariantPickerScriptRefId(templateId: string): string | null {
        return this.getTemplateVariantPickerCompilationState(templateId).templateScriptRefId;
    }

    private async waitForAsyncTemplateResult(jobId: string): Promise<Blob> {
        let job = await this.dataCrafterTemplatesService.getTemplateScriptRunJob(jobId);

        while (job.status !== JobStatus.Completed && job.status !== JobStatus.Failed) {
            await this.delay(this.templateJobPollingDelay);
            job = await this.dataCrafterTemplatesService.getTemplateScriptRunJob(jobId);
        }

        if (job.status === JobStatus.Failed) {
            throw new Error(job.error ?? 'Template script run job failed');
        }

        return this.dataCrafterTemplatesService.downloadTemplateScriptRunJobResult(jobId);
    }

    private async delay(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    private beginTemplateCompilation(templateId: string, incremental: boolean): void {
        const state: Partial<TemplateCompilationState> = {
            compilationProgress: {
                step: 1,
                total_steps: 1,
                message: incremental
                    ? 'Running incremental script compilation...\n'
                    : 'Initializing code generation...\nProcessing template configuration and preparing initial prompt.\n',
                status: 'in_progress',
                duration: 0
            },
            templateScriptError: undefined
        };

        if (incremental) {
            state.isLoadingIncrementalTemplateScript = true;
            state.incrementalCompilationMode = undefined;
            state.incrementalTemplateScriptError = undefined;
        } else {
            state.isLoadingTemplateScript = true;
        }

        this.setTemplateCompilationState(templateId, state);
    }

    private completeTemplateCompilation(
        templateId: string,
        incremental: boolean,
        state: Partial<TemplateCompilationState> = {}
    ): void {
        this.setTemplateCompilationState(templateId, {
            ...state,
            ...(incremental ? { isLoadingIncrementalTemplateScript: false } : { isLoadingTemplateScript: false }),
            compilationProgress: null
        });
    }

    private failTemplateCompilation(
        templateId: string,
        incremental: boolean,
        errorMessage: string,
        state: Partial<TemplateCompilationState> = {}
    ): void {
        this.completeTemplateCompilation(templateId, incremental, {
            ...state,
            templateScriptError: errorMessage,
            ...(incremental ? { incrementalTemplateScriptError: errorMessage } : {})
        });
    }

    private setCompiledTemplate(
        templateId: string,
        selectedOutputVariant: DataCrafterOutputVariant | undefined,
        scriptRefId: string,
        code: string | undefined
    ): void {
        const currentState = this.getTemplateCompilationState(templateId);

        this.setTemplateCompilationState(templateId, {
            templateScript: selectedOutputVariant ? currentState.templateScript : (code ?? currentState.templateScript),
            templateScriptRefId: selectedOutputVariant ? currentState.templateScriptRefId : scriptRefId
        });

        if (selectedOutputVariant) {
            selectedOutputVariant.scriptRefId = scriptRefId;
            selectedOutputVariant.code = code ?? selectedOutputVariant.code;
        }
    }

    private generateTempId(): string {
        return `${this.tempTemplateIdPrefix}${uuidv4()}`;
    }
}
